import { useId } from "react";

export type ToggleVariant = "default" | "brand" | "signature";

const VARIANT_ON_CLASSES: Record<ToggleVariant, string> = {
  default: "bg-content-primary",
  brand: "bg-brand-primary",
  signature: "bg-brand-gradient",
};

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;

  labelPosition?: "left" | "right";

  variant?: ToggleVariant;
  disabled?: boolean;
  id?: string;

  "aria-label"?: string;
}

export function Toggle({
  checked,
  onChange,
  label,
  labelPosition = "left",
  variant = "default",
  disabled = false,
  id,
  "aria-label": ariaLabel,
}: Readonly<ToggleProps>) {
  const autoId = useId();
  const resolvedId = id ?? autoId;

  const toggle = (
    <button
      id={resolvedId}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        "relative isolate inline-block h-5 w-9 shrink-0 rounded-full transition-colors",
        "btn-focus-ring",
        checked ? VARIANT_ON_CLASSES[variant] : "bg-surface-tertiary",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "absolute top-0.5 size-4 rounded-full bg-surface-elevated shadow-sm transition-all",
          checked ? "right-0.5" : "left-0.5",
        ].join(" ")}
      />
    </button>
  );

  if (!label) return toggle;

  return (
    <div className="flex items-center gap-2">
      {labelPosition === "left" && (
        <label htmlFor={resolvedId} className="text-sm font-medium text-content-secondary">
          {label}
        </label>
      )}
      {toggle}
      {labelPosition === "right" && (
        <label htmlFor={resolvedId} className="text-sm font-medium text-content-secondary">
          {label}
        </label>
      )}
    </div>
  );
}
